import { useMemo, useState } from 'react';
import { Card, Tabs, Button, Empty, Space, Switch, Popconfirm, Typography, App, Spin, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, SettingOutlined, HighlightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { FileImageOutlined } from '@ant-design/icons';
import { ManageCategoriesModal } from './ManageCategoriesModal';
import { useFlyerCategories, useFlyerTemplates, useUpdateFlyerTemplate, useDeleteFlyerTemplate } from '@/hooks/useFlyers';
import type { FlyerTemplate } from '@/types/flyer';

const nameOf = (j: Record<string, string> | null | undefined) => j?.en ?? Object.values(j ?? {})[0] ?? '(unnamed)';

export function FlyersPage() {
  const { message } = App.useApp();
  const nav = useNavigate();
  const catsQ = useFlyerCategories();
  const tplsQ = useFlyerTemplates();
  const update = useUpdateFlyerTemplate();
  const del = useDeleteFlyerTemplate();
  const [cat, setCat] = useState<string>('');
  const [manageOpen, setManageOpen] = useState(false);

  const cats = catsQ.data ?? [];
  const templates = useMemo(
    () => (tplsQ.data ?? []).filter((t) => !cat || t.category_id === cat),
    [tplsQ.data, cat],
  );

  return (
    <div style={{ maxWidth: 1200 }}>
      <PageHero icon={<FileImageOutlined />} title="Flyers" subtitle="Flyer templates — layout, background, program binding" />
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Tabs activeKey={cat} onChange={setCat}
          items={[{ key: '', label: 'All' }, ...cats.map((c) => ({ key: c.id, label: nameOf(c.name_json) }))]} />
        <Space>
          <Button icon={<HighlightOutlined />} onClick={() => nav('/flyers/compose')}>Composer</Button>
          <Button icon={<SettingOutlined />} onClick={() => setManageOpen(true)}>Manage categories</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => nav('/flyers/templates/new')}>New template</Button>
        </Space>
      </Space>
      {tplsQ.isLoading ? <Spin /> : templates.length === 0 ? <Empty description="No templates" /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {templates.map((t) => (
            <TemplateCard key={t.id} t={t}
              onOpen={() => nav(`/flyers/templates/${t.id}`)}
              onToggle={async (v) => { try { await update.mutateAsync({ id: t.id, b: { is_active: v } }); } catch (e) { message.error((e as Error).message); } }}
              onDelete={async () => { try { await del.mutateAsync(t.id); message.success('Deleted'); } catch (e) { message.error((e as Error).message); } }} />
          ))}
        </div>
      )}
      <ManageCategoriesModal open={manageOpen} onClose={() => setManageOpen(false)} />
    </div>
  );
}

function TemplateCard({ t, onOpen, onToggle, onDelete }: {
  t: FlyerTemplate; onOpen: () => void; onToggle: (v: boolean) => void; onDelete: () => void;
}) {
  return (
    <Card size="small" hoverable
      cover={
        <div onClick={onOpen} style={{ height: 160, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer' }}>
          {t.preview_image_url || t.background_image_url
            ? <img src={t.preview_image_url ?? t.background_image_url ?? undefined} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            : <Typography.Text type="secondary">{t.width}×{t.height}</Typography.Text>}
        </div>
      }>
      <Typography.Text strong ellipsis style={{ display: 'block', cursor: 'pointer' }} onClick={onOpen}>{nameOf(t.name_json)}</Typography.Text>
      <Space style={{ marginTop: 6, width: '100%', justifyContent: 'space-between' }}>
        <Space size={4}>
          <Switch size="small" checked={t.is_active} onChange={onToggle} />
          {t.source_topic_no && <Tag color="cyan">program</Tag>}
        </Space>
        <Popconfirm title={`Delete "${nameOf(t.name_json)}"?`} onConfirm={onDelete}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    </Card>
  );
}
